import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const Hero: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"]
    });

    // Scroll-driven animations
    const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, 10]);
    const xLeft = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const xRight = useTransform(scrollYProgress, [0, 1], [0, 100]);

    return (
        <section
            ref={ref}
            className="section bg-primary flex items-center justify-center relative overflow-hidden min-h-[95vh]"
            id="hero"
        >
            {/* Abstract background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl"
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.4, 0.3]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
                <motion.div
                    className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-accent/10 blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.2, 0.3, 0.2]
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />

                {/* Additional background elements */}
                <motion.div
                    className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-green-400/5 blur-3xl"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.3, 0.2]
                    }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
                <motion.div
                    className="absolute bottom-1/3 right-1/4 w-72 h-72 rounded-full bg-purple-400/5 blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.1, 0.2, 0.1]
                    }}
                    transition={{
                        duration: 9,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
            </div>

            {/* Grid lines for technical feel */}
            <div className="absolute inset-0 grid grid-cols-8 opacity-10 pointer-events-none">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="border-r border-black/10 h-full"></div>
                ))}
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="border-t border-black/10 w-full absolute"
                        style={{ top: `${(i + 1) * (100 / 8)}%` }}></div>
                ))}
            </div>

            {/* Animated particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {Array.from({ length: 15 }).map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 rounded-full bg-accent/30"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`
                        }}
                        animate={{
                            y: [0, Math.random() * 100 - 50],
                            x: [0, Math.random() * 100 - 50],
                            opacity: [0, 1, 0]
                        }}
                        transition={{
                            duration: 5 + Math.random() * 5,
                            repeat: Infinity,
                            delay: Math.random() * 5
                        }}
                    />
                ))}
            </div>

            <motion.div
                className="container-custom relative z-10 px-8"
                style={{ y, opacity, scale }}
            >
                <div className="flex flex-col md:flex-row items-center justify-between gap-16 py-10">
                    <motion.div
                        className="w-full md:w-6/12"
                        style={{ x: xLeft }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <motion.div
                                className="inline-block px-4 py-2 bg-accent/10 rounded-full text-accent text-sm font-medium mb-6"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Simple Yet Powerful
                            </motion.div>
                            <motion.h1
                                className="text-4xl md:text-6xl font-bold text-text-primary mb-6 leading-tight"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                Crypto Management, <span className="text-accent">Simplified</span>
                            </motion.h1>
                            <motion.p
                                className="text-text-secondary text-xl mb-8 max-w-xl leading-relaxed"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                Focus on what matters. Our AI-powered platform shows you exactly what you need to know, nothing more.
                            </motion.p>

                            {/* Key Benefits */}
                            <motion.div
                                className="grid grid-cols-3 gap-4 mb-8 max-w-lg"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                            >
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                                    <h3 className="text-accent text-2xl font-bold">Clear</h3>
                                    <p className="text-xs text-text-secondary">No clutter, just insights</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                                    <h3 className="text-accent text-2xl font-bold">Smart</h3>
                                    <p className="text-xs text-text-secondary">AI-powered decisions</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                                    <h3 className="text-accent text-2xl font-bold">Simple</h3>
                                    <p className="text-xs text-text-secondary">One-click actions</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex flex-wrap gap-5"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                            >
                                <motion.button
                                    className="px-8 py-3 bg-accent text-white rounded-lg font-medium shadow-lg"
                                    whileHover={{
                                        scale: 1.05,
                                        boxShadow: '0 0 20px rgba(0, 112, 243, 0.5)'
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Start Free Trial
                                </motion.button>
                                <motion.button
                                    className="px-8 py-3 border border-accent/30 text-accent rounded-lg font-medium"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    See How It Works
                                </motion.button>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="w-full md:w-6/12 relative"
                        style={{ x: xRight }}
                    >
                        {/* Floating Elements */}
                        <motion.div
                            className="absolute -top-8 -right-8 w-16 h-16 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-3 group z-[100]"
                            animate={{
                                y: [0, -10, 0],
                                rotate: [0, 5, 0]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                repeatType: "reverse"
                            }}
                        >
                            <div className="flex items-center justify-center h-full">
                                <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                            </div>
                            <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 w-48 bg-white/90 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-[100]">
                                <div className="text-sm font-medium text-gray-900 mb-1">Real-time Analytics</div>
                                <p className="text-xs text-gray-600">Track your portfolio's performance with precision</p>
                                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white/90 rotate-45 border-r border-b border-white/20"></div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="absolute top-1/4 -left-6 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-white/20 p-2 group z-[100]"
                            animate={{
                                y: [0, 10, 0],
                                rotate: [0, -5, 0]
                            }}
                            transition={{
                                duration: 5,
                                repeat: Infinity,
                                repeatType: "reverse"
                            }}
                        >
                            <div className="flex items-center justify-center h-full">
                                <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <div className="absolute top-1/2 -right-48 transform -translate-y-1/2 w-44 bg-white/90 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-[100]">
                                <div className="text-sm font-medium text-gray-900 mb-1">Smart Insights</div>
                                <p className="text-xs text-gray-600">AI-powered analysis for smarter decisions</p>
                                <div className="absolute top-1/2 -left-2 transform -translate-y-1/2 w-4 h-4 bg-white/90 rotate-45 border-l border-b border-white/20"></div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="absolute bottom-1/4 -right-4 w-14 h-14 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-3 group z-[100]"
                            animate={{
                                y: [0, -8, 0],
                                rotate: [0, 3, 0]
                            }}
                            transition={{
                                duration: 4.5,
                                repeat: Infinity,
                                repeatType: "reverse"
                            }}
                        >
                            <div className="flex items-center justify-center h-full">
                                <svg className="w-7 h-7 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-48 bg-white/90 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-[100]">
                                <div className="text-sm font-medium text-gray-900 mb-1">Secure Investment</div>
                                <p className="text-xs text-gray-600">Your assets are protected with enterprise-grade security</p>
                                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white/90 rotate-45 border-t border-l border-white/20"></div>
                            </div>
                        </motion.div>

                        {/* Connection Lines */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <motion.path
                                d="M20,50 Q50,20 80,50"
                                fill="none"
                                stroke="rgba(99, 102, 241, 0.1)"
                                strokeWidth="0.5"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                            />
                            <motion.path
                                d="M30,70 Q50,40 70,70"
                                fill="none"
                                stroke="rgba(99, 102, 241, 0.1)"
                                strokeWidth="0.5"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse" }}
                            />
                        </svg>

                        {/* Simplified Dashboard Preview */}
                        <div className="relative w-full max-w-md mx-auto">
                            {/* Decorative elements */}
                            <motion.div
                                className="absolute -top-4 -right-4 w-24 h-24 bg-accent/10 rounded-full blur-2xl"
                                animate={{
                                    scale: [1, 1.2, 1],
                                    opacity: [0.3, 0.5, 0.3]
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    repeatType: "reverse"
                                }}
                            />
                            <motion.div
                                className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-400/10 rounded-full blur-2xl"
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [0.2, 0.4, 0.2]
                                }}
                                transition={{
                                    duration: 5,
                                    repeat: Infinity,
                                    repeatType: "reverse"
                                }}
                            />

                            <motion.div
                                className="w-full bg-white/90 backdrop-blur-md rounded-2xl shadow-xl overflow-hidden border border-white/20"
                                animate={{
                                    y: [0, -5, 0],
                                    boxShadow: [
                                        '0 20px 40px rgba(0, 0, 0, 0.1)',
                                        '0 20px 50px rgba(0, 0, 0, 0.15)',
                                        '0 20px 40px rgba(0, 0, 0, 0.1)'
                                    ]
                                }}
                                transition={{ duration: 5, repeat: Infinity }}
                            >
                                {/* Portfolio Summary */}
                                <div className="p-6">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">Your Portfolio</h3>
                                            <p className="text-sm text-gray-500">Last updated 2 min ago</p>
                                        </div>
                                        <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                                            <span className="text-sm text-green-600 font-medium">+2.4%</span>
                                            <span className="text-xs text-green-600">24h</span>
                                        </div>
                                    </div>

                                    {/* Total Value with Trend Line */}
                                    <div className="mb-6 relative">
                                        <p className="text-3xl font-bold text-gray-900">$124,563.89</p>
                                        <p className="text-sm text-gray-500">Total Value</p>
                                        <div className="absolute right-0 top-0 w-24 h-12">
                                            <svg viewBox="0 0 100 40" className="w-full h-full">
                                                <path
                                                    d="M0 35 C20 35, 30 20, 50 20 C70 20, 80 5, 100 5"
                                                    fill="none"
                                                    stroke="url(#gradient)"
                                                    strokeWidth="2"
                                                />
                                                <defs>
                                                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                        <stop offset="0%" stopColor="#10B981" />
                                                        <stop offset="100%" stopColor="#059669" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Top 3 Assets with Enhanced UI */}
                                    <div className="space-y-4">
                                        {[
                                            { name: 'Bitcoin', value: '$56,289.45', change: '+2.4%', trend: 'up' },
                                            { name: 'Ethereum', value: '$31,245.67', change: '+1.8%', trend: 'up' },
                                            { name: 'Solana', value: '$19,178.90', change: '+5.2%', trend: 'up' }
                                        ].map((asset, index) => (
                                            <motion.div
                                                key={index}
                                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                                                whileHover={{ scale: 1.02 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === 0 ? 'bg-orange-100' :
                                                        index === 1 ? 'bg-blue-100' :
                                                            'bg-purple-100'
                                                        }`}>
                                                        <span className={`text-sm font-medium ${index === 0 ? 'text-orange-600' :
                                                            index === 1 ? 'text-blue-600' :
                                                                'text-purple-600'
                                                            }`}>
                                                            {asset.name.charAt(0)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{asset.name}</p>
                                                        <p className="text-xs text-gray-500">{asset.value}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-sm font-medium ${asset.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        {asset.change}
                                                    </span>
                                                    <div className={`w-2 h-2 rounded-full ${asset.trend === 'up' ? 'bg-green-500' : 'bg-red-500'
                                                        }`} />
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* AI Insight with Enhanced UI */}
                                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-100">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h5a1 1 0 01.82 1.573l-7 10A1 1 0 019 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">AI Market Insight</p>
                                                <p className="text-xs text-gray-600 mt-1">Strong buy signal for BTC with 92% confidence</p>
                                                <div className="mt-2 flex items-center space-x-2">
                                                    <div className="h-1.5 w-24 bg-blue-200 rounded-full overflow-hidden">
                                                        <div className="h-full w-[92%] bg-blue-600 rounded-full" />
                                                    </div>
                                                    <span className="text-xs text-blue-600 font-medium">92%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
                className="absolute bottom-6 left-1/2 transform -translate-x-1/2"
                animate={{
                    y: [0, 10, 0],
                    opacity: [0.5, 1, 0.5]
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity
                }}
            >
                <div className="w-6 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center">
                    <motion.div
                        className="w-1 h-2 bg-gray-400 rounded-full"
                        animate={{
                            y: [0, 4, 0]
                        }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity
                        }}
                    />
                </div>
            </motion.div>
        </section>
    );
};

export default Hero; 